import { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTheme } from '@mui/material/styles';

// ant design icons
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DisconnectOutlined from '@ant-design/icons/DisconnectOutlined';
import MinusOutlined from '@ant-design/icons/MinusOutlined';
import LeftOutlined from '@ant-design/icons/LeftOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import FilePdfOutlined from '@ant-design/icons/FilePdfOutlined';
import TrophyOutlined from '@ant-design/icons/TrophyOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';

// third party
import { format, eachDayOfInterval, subDays } from 'date-fns';

// project imports
import MainCard from 'components/MainCard';
import { HistoryEmptyState } from 'components/HistoryPagination';
import useDatasheetHistory, { latestPerDay } from 'hooks/useDatasheetHistory';
import { CSVExport } from 'components/third-party/react-table';
import { openSnackbar } from 'api/snackbar';
import useOnlineStatus from 'hooks/useOnlineStatus';
// Controlling prompt levels + the per-target default helper. Shared with the
// Goals & Targets dialog and MultiTrialMode so the code list never drifts.
import { PROMPT_LEVELS, defaultPromptsForTarget } from 'utils/promptLevels';
import SpeechTextField from 'components/SpeechTextField';

// Historical data is loaded from the API; start with empty state

const EMPTY_TARGETS = [];

// Session display: prefix a target's name with its domain code, colon-separated
// (e.g. "LR: Sit down"). Domain values arrive prefixed like "DOMAIN-LR", so strip
// that leading "DOMAIN-" and show just the code. Display-only — the saved targetName
// stays plain.
const targetLabel = (t) => {
  const name = (t && t.name) || '';
  const domain = ((t && t.domain) || '').replace(/^domain[-_\s]*/i, '').trim();
  return domain ? `${domain}: ${name}` : name;
};

// Shared styling for the SD (discriminative stimulus) tooltip — a clean, light
// "card" look with readable text instead of the default small dark tooltip.
const SD_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      bgcolor: 'grey.900',
      color: 'common.white',
      fontSize: '0.9rem',
      fontWeight: 500,
      lineHeight: 1.5,
      whiteSpace: 'pre-line',
      px: 1.5,
      py: 1,
      maxWidth: 340,
      borderRadius: 1.5,
      boxShadow: 3
    }
  },
  arrow: { sx: { color: 'grey.900' } }
};

const ColdProbeMode = ({
  viewMode = 'individual',
  onDataChange,
  targets = EMPTY_TARGETS,
  // Offline mode props
  isOfflineMode = false,
  childId,
  childName,
  therapistId,
  therapistName,
  sessionId,
  onSave,
  onBack
}) => {
  const theme = useTheme();
  const intl = useIntl();

  // Get actual network status - auto-detect offline regardless of isOfflineMode prop
  const { isOnline } = useOnlineStatus();

  const activeTargets = targets;

  const [currentTarget, setCurrentTarget] = useState(0);
  // When every target has a response we show a completion card instead of the target card.
  // `reviewing` lets the therapist go back to the targets to check/change responses.
  const [reviewing, setReviewing] = useState(false);
  const [responses, setResponses] = useState(new Array(activeTargets.length).fill(null));
  // Controlling prompts per target — an ARRAY of PROMPT_LEVELS codes (multi-select).
  // Kept parallel to `responses` so each target tracks both its response and the
  // prompt(s) used. Seeded from ALL of the target's Default Controlling Prompts
  // (set in Goals & Targets) so the therapist doesn't re-pick the usual prompts
  // every session; still fully overridable (tick/untick any).
  const [promptLevels, setPromptLevels] = useState(activeTargets.map(defaultPromptsForTarget));
  const [notes, setNotes] = useState(new Array(activeTargets.length).fill(''));
  const [sessionNotes, setSessionNotes] = useState('');
  const [tabValue, setTabValue] = useState(0);
  /* Today's responses as they are entered — a LOCAL OVERLAY on the saved history
   * below, not the history itself. Kept so the Historical tab reflects the session
   * in progress before it has been submitted. */
  const [historicalData, setHistoricalData] = useState({});
  /* The window the Historical grid draws. The history fetch is bounded by it. */
  const [startDate, setStartDate] = useState(subDays(new Date(), 6)); // Last 7 days
  const [endDate, setEndDate] = useState(new Date());

  /* Previously submitted entries. Without this the Historical tab only ever showed
   * the current browser session's work.
   *
   * Fetched BY DATE RANGE, not by page: the grid draws one column per day in the
   * window above, so a page of five arbitrary entries would leave columns empty on
   * days that do have data. The window is the bound — a week is a handful of rows. */
  const { records: historyRecords, isEmpty: historyIsEmpty } = useDatasheetHistory(childId, 'cold_probe', {
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd')
  });

  // day -> { [targetId]: response }, latest entry per day winning.
  const savedHistory = useMemo(
    () =>
      latestPerDay(historyRecords, (record) => {
        const recTargets = record.raw_data?.targets;
        if (!Array.isArray(recTargets)) return undefined;
        const byTarget = {};
        recTargets.forEach((t) => {
          if (t?.targetId) byTarget[t.targetId] = t.response || '';
        });
        return byTarget;
      }),
    [historyRecords]
  );

  // Unsubmitted local edits win over the saved copy for the same day and target.
  const mergedHistory = useMemo(() => {
    const out = { ...savedHistory };
    Object.entries(historicalData).forEach(([day, byTarget]) => {
      out[day] = { ...(out[day] || {}), ...byTarget };
    });
    return out;
  }, [savedHistory, historicalData]);
  const [imagePopup, setImagePopup] = useState({ open: false, target: null });
  const [saving, setSaving] = useState(false);

  // Ref to skip auto-save on initial render
  const initialRenderRef = useRef(true);

  // Handle image popup
  // eslint-disable-next-line no-unused-vars
  const handleOpenImagePopup = (target) => {
    if (target.image) {
      setImagePopup({ open: true, target });
    }
  };

  const handleCloseImagePopup = () => {
    setImagePopup({ open: false, target: null });
  };

  // Reset state when targets change
  useEffect(() => {
    setCurrentTarget(0);
    setReviewing(false);
    setResponses(new Array(activeTargets.length).fill(null));
    // Re-seed prompts from each target's Default Controlling Prompts (all of them).
    setPromptLevels(activeTargets.map(defaultPromptsForTarget));
    setNotes(new Array(activeTargets.length).fill(''));
    setHistoricalData({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets]);

  // Auto-save: notify parent when form data changes so it can debounce and persist
  useEffect(() => {
    // Skip the initial render to avoid saving empty/default state
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Don't notify if no data has been entered yet
    const completed = responses.filter((r) => r !== null).length;
    if (completed === 0) return;

    // Collect current form data (mirrors handleSave structure)
    const formData = {
      targets: activeTargets.map((target, index) => ({
        targetId: target.id,
        targetName: target.name,
        response: responses[index],
        // Array of controlling-prompt codes (multi-select). Empty array = none picked.
        promptLevel: Array.isArray(promptLevels[index]) ? promptLevels[index] : [],
        notes: notes[index] || ''
      })),
      sessionNotes,
      completedCount: completed,
      totalTargets: activeTargets.length
    };

    if (onDataChange) onDataChange('cold-probe', formData);
  }, [responses, promptLevels, notes, sessionNotes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine if we should save offline (either explicit offline mode OR network is down)
  // eslint-disable-next-line no-unused-vars
  const shouldSaveOffline = isOfflineMode || !isOnline;

  // Handle explicit save — triggers the parent's auto-save via onDataChange (upsert pattern).
  // Does NOT call saveDataEntry directly to avoid creating duplicate IndexedDB entries.
  const handleSave = async (showNotification = true) => {
    setSaving(true);
    try {
      const completed = responses.filter((r) => r !== null).length;

      // Don't save if no data has been entered
      if (completed === 0) {
        setSaving(false);
        return;
      }

      const formData = {
        targets: activeTargets.map((target, index) => ({
          targetId: target.id,
          targetName: target.name,
          response: responses[index],
          promptLevel: promptLevels[index] || '',
          notes: notes[index] || ''
        })),
        sessionNotes,
        completedCount: completed,
        totalTargets: activeTargets.length
      };

      // Notify parent to persist via its upsert pattern (persistToIndexedDB)
      if (onDataChange) {
        onDataChange('cold-probe', formData);
      }

      if (showNotification) {
        openSnackbar({
          open: true,
          message: isOnline
            ? intl.formatMessage({ id: 'form.msg.dataSaved' }, { formName: intl.formatMessage({ id: 'form.coldProbe.title' }) })
            : intl.formatMessage({ id: 'form.msg.dataSavedOffline' }, { formName: intl.formatMessage({ id: 'form.coldProbe.title' }) }),
          variant: 'alert',
          alert: { color: 'success' }
        });
      }

      if (onSave) onSave();
      if (isOfflineMode && onBack) onBack();
    } catch (error) {
      console.error('Error saving entry:', error);
      if (showNotification) {
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'form.msg.saveFailed' }),
          variant: 'alert',
          alert: { color: 'error' }
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // Alias for backward compatibility with existing UI buttons
  const handleOfflineSave = handleSave;

  // Auto-advance to the next target — only when the response is picked AND at
  // least one controlling prompt is selected for the current target. `promptForTarget`
  // is the target's prompt-code array (multi-select).
  const maybeAdvance = (responseForTarget, promptForTarget) => {
    const hasPrompt = Array.isArray(promptForTarget) ? promptForTarget.length > 0 : Boolean(promptForTarget);
    if (!responseForTarget || !hasPrompt) return;
    if (currentTarget < activeTargets.length - 1) {
      setTimeout(() => setCurrentTarget(currentTarget + 1), 300);
    }
  };

  const handleResponse = (response) => {
    // Toggle: clicking the already-selected response clears it (deselect → '').
    const nextValue = responses[currentTarget] === response ? '' : response;
    const newResponses = [...responses];
    newResponses[currentTarget] = nextValue;
    setResponses(newResponses);

    // Update historical data with today's entry
    const todayKey = format(new Date(), 'yyyy-MM-dd');
    setHistoricalData((prev) => {
      const updated = { ...prev };
      if (!updated[todayKey]) {
        updated[todayKey] = {};
      }
      updated[todayKey][activeTargets[currentTarget].id] = nextValue;
      return updated;
    });

    // Auto-save is handled by the useEffect on [responses, notes, sessionNotes]
    // which calls onDataChange → parent's triggerAutoSave → persistToIndexedDB (upsert)

    // Only advance on a real selection — never when deselecting.
    maybeAdvance(nextValue, promptLevels[currentTarget]);
  };

  // Multi-select: `value` is the full array of selected prompt codes for the
  // current target (from the non-exclusive ToggleButtonGroup).
  const handlePromptLevelChange = (value) => {
    const nextArray = Array.isArray(value) ? value : value ? [value] : [];
    const prevArray = Array.isArray(promptLevels[currentTarget]) ? promptLevels[currentTarget] : [];
    const nextPromptLevels = [...promptLevels];
    nextPromptLevels[currentTarget] = nextArray;
    setPromptLevels(nextPromptLevels);
    // Only auto-advance on the empty → non-empty transition (the first prompt
    // picked when a response is already set), never on subsequent multi-toggles.
    if (prevArray.length === 0 && nextArray.length > 0) maybeAdvance(responses[currentTarget], nextArray);
  };

  const handleGridResponse = (index, response) => {
    const newResponses = [...responses];
    newResponses[index] = response;
    setResponses(newResponses);

    // Update historical data with today's entry
    const todayKey = format(new Date(), 'yyyy-MM-dd');
    setHistoricalData((prev) => {
      const updated = { ...prev };
      if (!updated[todayKey]) {
        updated[todayKey] = {};
      }
      updated[todayKey][activeTargets[index].id] = response;
      return updated;
    });

    // Auto-save is handled by the useEffect on [responses, notes, sessionNotes]
    // which calls onDataChange → parent's triggerAutoSave → persistToIndexedDB (upsert)
  };

  const completed = responses.filter((r) => r !== null).length;
  const progress = Math.round((completed / activeTargets.length) * 100);

  // Get filtered date range for historical view
  const dateRange = useMemo(() => {
    if (!startDate || !endDate) return [];
    return eachDayOfInterval({ start: startDate, end: endDate });
  }, [startDate, endDate]);

  // Check if a skill is mastered (3 consecutive correct responses)
  const isSkillMastered = (targetId) => {
    const sortedDates = dateRange.map((date) => format(date, 'yyyy-MM-dd')).sort();
    let consecutiveCount = 0;

    for (const dateKey of sortedDates) {
      const response = mergedHistory[dateKey]?.[targetId];
      if (response === 'correct') {
        consecutiveCount++;
        if (consecutiveCount >= 3) {
          return true;
        }
      } else if (response) {
        // Reset if there's any other response (incorrect or no-response)
        consecutiveCount = 0;
      }
    }
    return false;
  };

  // Render response icon
  const renderResponseIcon = (response) => {
    if (response === 'correct') {
      return <CheckOutlined style={{ color: theme.palette.success.main, fontSize: '1.2rem' }} />;
    } else if (response === 'incorrect') {
      return <CloseOutlined style={{ color: theme.palette.error.main, fontSize: '1.2rem' }} />;
    } else if (response === 'no-response') {
      return <MinusOutlined style={{ color: theme.palette.grey[400], fontSize: '1.2rem' }} />;
    }
    return '-';
  };

  // Export to CSV
  const exportToCSV = () => {
    const csvData = activeTargets.map((target) => {
      const row = { Target: target.name };
      dateRange.forEach((date) => {
        const dateKey = format(date, 'yyyy-MM-dd');
        const dateLabel = format(date, 'dd/MM');
        const response = mergedHistory[dateKey]?.[target.id];
        row[dateLabel] = response === 'correct' ? '✓' : response === 'incorrect' ? '✗' : 'NA';
      });
      return row;
    });
    return csvData;
  };

  // Export to PDF (placeholder for future implementation)
  const exportToPDF = () => {
    // TODO: Implement PDF export functionality
  };

  // Empty state — no targets configured for this child
  if (activeTargets.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {intl.formatMessage({ id: 'form.msg.noTargets' })}
        </Alert>
      </Box>
    );
  }

  // Individual View
  if (viewMode === 'individual') {
    const target = activeTargets[currentTarget];

    return (
      <Box sx={isOfflineMode ? { p: { xs: 2, sm: 3 } } : {}}>
        {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
        {!isOfflineMode && !isOnline && (
          <Alert severity="info" icon={<DisconnectOutlined />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{intl.formatMessage({ id: 'form.msg.offlineNote' })}</strong>
            </Typography>
          </Alert>
        )}

        {/* Offline Mode Header */}
        {isOfflineMode && (
          <MainCard sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="text" startIcon={<LeftOutlined />} onClick={onBack}>
                  {intl.formatMessage({ id: 'form.button.back' })}
                </Button>
                <Box>
                  <Typography variant="h5">
                    {intl.formatMessage({ id: 'form.coldProbe.title' })} - {childName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {intl.formatMessage({ id: 'form.label.offlineMode' })}
                  </Typography>
                </Box>
              </Stack>
              <Button variant="contained" startIcon={<SaveOutlined />} onClick={handleOfflineSave} disabled={saving || completed === 0}>
                {saving ? intl.formatMessage({ id: 'form.button.saving' }) : intl.formatMessage({ id: 'form.button.saveOffline' })}
              </Button>
            </Stack>
          </MainCard>
        )}

        <Stack spacing={3}>
          {/* Session Progress */}
          <Box sx={{ width: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {intl.formatMessage({ id: 'form.label.sessionProgress' })}
              </Typography>
              <Typography variant="caption" color="primary" fontWeight={600}>
                {intl.formatMessage({ id: 'form.label.progressCount' }, { completed, total: activeTargets.length, progress })}
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
          </Box>

          {/* When all targets have a response, show a completion card in place of the target.
              "Review responses" returns to the targets so the therapist can check/change them. */}
          {completed === activeTargets.length && !reviewing ? (
            <MainCard>
              <Stack spacing={1.5} alignItems="center" sx={{ py: 5, textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: 'success.lighter',
                    color: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <TrophyOutlined style={{ fontSize: 30 }} />
                </Box>
                <Typography variant="h4">
                  {intl.formatMessage({
                    id: 'form.coldProbe.allTargetsComplete',
                    defaultMessage: 'All targets for this Cold Probe session are completed.'
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({
                    id: 'form.coldProbe.allTargetsCompleteSub',
                    defaultMessage: 'Review your responses if needed, or end the session.'
                  })}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setReviewing(true);
                    setCurrentTarget(0);
                  }}
                  sx={{ mt: 1 }}
                >
                  {intl.formatMessage({ id: 'form.coldProbe.reviewTargets', defaultMessage: 'Review responses' })}
                </Button>
              </Stack>
            </MainCard>
          ) : (
            <>
              {completed === activeTargets.length && reviewing && (
                <Box sx={{ textAlign: 'right' }}>
                  <Button variant="text" size="small" startIcon={<CheckOutlined />} onClick={() => setReviewing(false)}>
                    {intl.formatMessage({ id: 'form.coldProbe.backToSummary', defaultMessage: 'Back to summary' })}
                  </Button>
                </Box>
              )}
              {/* Data Collection Card */}
              <MainCard>
                <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
                  {/* Prev / Next icon-only buttons flank the "Target X of Y"
                  counter so their position stays fixed regardless of target
                  name length. Each button hides when navigation in that
                  direction isn't possible. */}
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setCurrentTarget((i) => Math.max(0, i - 1))}
                      disabled={currentTarget === 0}
                      aria-label={intl.formatMessage({ id: 'form.button.previous', defaultMessage: 'Previous' })}
                      sx={{ visibility: currentTarget === 0 ? 'hidden' : 'visible', minWidth: 32, px: 0.75 }}
                    >
                      <LeftOutlined />
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.label.targetOfTotal' }, { current: currentTarget + 1, total: activeTargets.length })}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setCurrentTarget((i) => Math.min(activeTargets.length - 1, i + 1))}
                      disabled={currentTarget >= activeTargets.length - 1}
                      aria-label={intl.formatMessage({ id: 'form.button.next', defaultMessage: 'Next' })}
                      sx={{ visibility: currentTarget >= activeTargets.length - 1 ? 'hidden' : 'visible', minWidth: 32, px: 0.75 }}
                    >
                      <RightOutlined />
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                    <Typography variant="h3" textAlign="center" fontWeight={700}>
                      {targetLabel(target)}
                    </Typography>
                    {target.sdInstruction && (
                      <Tooltip title={target.sdInstruction} placement="top" arrow slotProps={SD_TOOLTIP_SLOT_PROPS}>
                        <InfoCircleOutlined style={{ fontSize: '1.6rem', color: theme.palette.primary.main, cursor: 'pointer' }} />
                      </Tooltip>
                    )}
                  </Stack>

                  {/* Controlling Prompt(s) — multi-select; therapist taps every
                  prompt code used for the child to respond. Pre-selected from the
                  target's Default Controlling Prompts. Persisted (as an array)
                  alongside the Correct / Incorrect / N/A response. Auto-advance to
                  the next target requires the response AND ≥1 prompt selected. */}
                  <Stack alignItems="center" spacing={1} sx={{ mt: 1, width: '100%', maxWidth: 600 }}>
                    <Typography
                      variant="caption"
                      color={
                        (promptLevels[currentTarget] || []).length === 0 && responses[currentTarget] ? 'warning.main' : 'text.secondary'
                      }
                      fontWeight={600}
                    >
                      {intl.formatMessage({ id: 'form.coldProbe.promptLevel', defaultMessage: 'Controlling Prompt' })}
                      {(promptLevels[currentTarget] || []).length === 0 && responses[currentTarget] && ' • required to continue'}
                    </Typography>
                    <ToggleButtonGroup
                      value={promptLevels[currentTarget] || []}
                      size="small"
                      onChange={(_, v) => handlePromptLevelChange(v)}
                      sx={{ flexWrap: 'wrap', gap: 0.5 }}
                    >
                      {PROMPT_LEVELS.map((p) => (
                        <ToggleButton
                          key={p.code}
                          value={p.code}
                          sx={{
                            px: 1.5,
                            fontWeight: 700,
                            '&.Mui-selected': {
                              bgcolor: 'primary.main',
                              color: 'common.white',
                              '&:hover': { bgcolor: 'primary.dark' }
                            }
                          }}
                          title={p.label}
                        >
                          {p.code}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Stack>

                  {/* Response Buttons */}
                  <Grid container spacing={2} sx={{ maxWidth: 600, mt: 1 }}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Button
                        variant={responses[currentTarget] === 'correct' ? 'contained' : 'outlined'}
                        color="success"
                        fullWidth
                        size="medium"
                        onClick={() => handleResponse('correct')}
                        sx={{ py: 2, fontSize: '0.95rem', fontWeight: 600 }}
                      >
                        <Stack spacing={0.5} alignItems="center">
                          <CheckOutlined style={{ fontSize: '1.5rem' }} />
                          <span>{intl.formatMessage({ id: 'form.coldProbe.response.correct' })}</span>
                        </Stack>
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Button
                        variant={responses[currentTarget] === 'incorrect' ? 'contained' : 'outlined'}
                        color="error"
                        fullWidth
                        size="medium"
                        onClick={() => handleResponse('incorrect')}
                        sx={{ py: 2, fontSize: '0.95rem', fontWeight: 600 }}
                      >
                        <Stack spacing={0.5} alignItems="center">
                          <CloseOutlined style={{ fontSize: '1.5rem' }} />
                          <span>{intl.formatMessage({ id: 'form.coldProbe.response.incorrect' })}</span>
                        </Stack>
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Button
                        variant={responses[currentTarget] === 'no-response' ? 'contained' : 'outlined'}
                        color="secondary"
                        fullWidth
                        size="medium"
                        onClick={() => handleResponse('no-response')}
                        sx={{ py: 2, fontSize: '0.95rem', fontWeight: 600 }}
                      >
                        <Stack spacing={0.5} alignItems="center">
                          <MinusOutlined style={{ fontSize: '1.5rem' }} />
                          <span>{intl.formatMessage({ id: 'common.notAvailable' })}</span>
                        </Stack>
                      </Button>
                    </Grid>
                  </Grid>
                </Stack>
              </MainCard>
            </>
          )}

          {/* Session Notes - Offline Mode Only */}
          {isOfflineMode && (
            <MainCard>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {intl.formatMessage({ id: 'form.label.sessionNotes' })}
              </Typography>
              <SpeechTextField
                fullWidth
                multiline
                rows={3}
                placeholder={intl.formatMessage({ id: 'form.label.notesPlaceholder' })}
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
              />
            </MainCard>
          )}

          {/* Image Popup Dialog - Only Image (3/4 of screen) */}
          <Dialog
            open={imagePopup.open}
            onClose={handleCloseImagePopup}
            maxWidth={false}
            PaperProps={{
              sx: {
                bgcolor: 'transparent',
                boxShadow: 'none',
                overflow: 'visible',
                m: 0
              }
            }}
          >
            {imagePopup.target && (
              <Box
                component="img"
                src={imagePopup.target.image}
                alt={imagePopup.target.name}
                sx={{
                  width: '75vw',
                  height: '75vh',
                  objectFit: 'contain',
                  borderRadius: 2,
                  display: 'block'
                }}
              />
            )}
          </Dialog>
        </Stack>
      </Box>
    );
  }

  // Grid View
  return (
    <MainCard>
      {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
      {!isOfflineMode && !isOnline && (
        <Alert severity="info" icon={<DisconnectOutlined />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{intl.formatMessage({ id: 'form.msg.offlineNote' })}</strong>
          </Typography>
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={intl.formatMessage({ id: 'form.tab.dataEntry' })} />
        <Tab label={intl.formatMessage({ id: 'form.tab.historicalData' })} />
      </Tabs>

      <Box sx={{ pt: 3 }}>
        {/* Tab 0: Data Entry */}
        {tabValue === 0 && (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={50}>#</TableCell>
                    <TableCell width={250}>{intl.formatMessage({ id: 'form.label.target' })}</TableCell>
                    <TableCell width={200}>{intl.formatMessage({ id: 'form.label.response' })}</TableCell>
                    <TableCell width={160}>
                      {intl.formatMessage({ id: 'form.coldProbe.promptLevel', defaultMessage: 'Prompt Level' })}
                    </TableCell>
                    <TableCell>{intl.formatMessage({ id: 'form.label.notes' })}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeTargets.map((target, index) => (
                    <TableRow key={target.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {targetLabel(target)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {/* Strong selected styling so the chosen response stands
                            out from the unselected options at a glance. */}
                        <ToggleButtonGroup
                          value={responses[index]}
                          exclusive
                          onChange={(e, value) => value && handleGridResponse(index, value)}
                          size="small"
                        >
                          <ToggleButton
                            value="correct"
                            color="success"
                            sx={{
                              '&.Mui-selected': {
                                bgcolor: 'success.main',
                                color: 'common.white',
                                '&:hover': { bgcolor: 'success.dark' }
                              }
                            }}
                          >
                            <CheckOutlined />
                          </ToggleButton>
                          <ToggleButton
                            value="incorrect"
                            color="error"
                            sx={{
                              '&.Mui-selected': {
                                bgcolor: 'error.main',
                                color: 'common.white',
                                '&:hover': { bgcolor: 'error.dark' }
                              }
                            }}
                          >
                            <CloseOutlined />
                          </ToggleButton>
                          <ToggleButton
                            value="no-response"
                            color="standard"
                            sx={{
                              '&.Mui-selected': {
                                bgcolor: 'grey.600',
                                color: 'common.white',
                                '&:hover': { bgcolor: 'grey.700' }
                              }
                            }}
                          >
                            <MinusOutlined />
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </TableCell>
                      <TableCell>
                        {/* Per-row controlling-prompt selector — multi-select so
                            the row can carry several prompt codes; shows the picked
                            codes joined (e.g. "PVP, IND"), or "—" when empty. */}
                        <Select
                          size="small"
                          multiple
                          displayEmpty
                          value={Array.isArray(promptLevels[index]) ? promptLevels[index] : []}
                          onChange={(e) => {
                            const next = [...promptLevels];
                            next[index] = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                            setPromptLevels(next);
                          }}
                          renderValue={(selected) => ((selected || []).length ? selected.join(', ') : <em>—</em>)}
                          sx={{ minWidth: 110, '& .MuiSelect-select': { py: 0.5 } }}
                        >
                          {PROMPT_LEVELS.map((p) => (
                            <MenuItem key={p.code} value={p.code} title={p.label}>
                              <Checkbox size="small" checked={(promptLevels[index] || []).includes(p.code)} sx={{ p: 0.5 }} />
                              {p.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={notes[index]}
                          onChange={(e) => {
                            const newNotes = [...notes];
                            newNotes[index] = e.target.value;
                            setNotes(newNotes);

                            // Trigger auto-save
                            if (onDataChange) {
                              onDataChange();
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Tab 1: Historical Data */}
        {tabValue === 1 && (
          <>
            {/* Date Filters and Export */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={intl.formatMessage({ id: 'form.label.startDate' })}
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 180 }
                    }
                  }}
                />
                <DatePicker
                  label={intl.formatMessage({ id: 'form.label.endDate' })}
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 180 }
                    }
                  }}
                />
              </LocalizationProvider>

              <Box sx={{ flexGrow: 1 }} />

              <Stack direction="row" spacing={1}>
                <CSVExport data={exportToCSV()} filename={`cold-probe-history-${format(new Date(), 'yyyy-MM-dd')}.csv`} />
                <Button variant="outlined" size="small" startIcon={<FilePdfOutlined />} onClick={exportToPDF}>
                  {intl.formatMessage({ id: 'form.button.exportPdf' })}
                </Button>
              </Stack>
            </Stack>

            {/* Historical Data Grid */}
            {/* Nothing saved in the picked window and nothing entered today — a grid
                of dashes says less than saying so. */}
            {historyIsEmpty && Object.keys(historicalData).length === 0 ? (
              <HistoryEmptyState />
            ) : (
              <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          bgcolor: 'background.paper',
                          zIndex: 3,
                          minWidth: 200,
                          fontWeight: 600,
                          borderRight: 2,
                          borderColor: 'divider'
                        }}
                      >
                        {intl.formatMessage({ id: 'form.label.target' })}
                      </TableCell>
                      {dateRange.map((date) => (
                        <TableCell
                          key={format(date, 'yyyy-MM-dd')}
                          align="center"
                          sx={{
                            minWidth: 80,
                            fontWeight: 600,
                            bgcolor:
                              format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') ? 'primary.lighter' : 'background.paper'
                          }}
                        >
                          {format(date, 'dd/MM')}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeTargets.map((target) => {
                      const isMastered = isSkillMastered(target.id);
                      return (
                        <TableRow key={target.id} hover>
                          <TableCell
                            sx={{
                              position: 'sticky',
                              left: 0,
                              bgcolor: 'background.paper',
                              zIndex: 2,
                              fontWeight: 600,
                              borderRight: 2,
                              borderColor: 'divider'
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Tooltip title={target.description} placement="right">
                                <Typography variant="body2" fontWeight={600}>
                                  {targetLabel(target)}
                                </Typography>
                              </Tooltip>
                              {target.sdInstruction && (
                                <Tooltip title={target.sdInstruction} placement="top" arrow slotProps={SD_TOOLTIP_SLOT_PROPS}>
                                  <InfoCircleOutlined
                                    style={{ fontSize: '1.3rem', color: theme.palette.primary.main, cursor: 'pointer' }}
                                  />
                                </Tooltip>
                              )}
                              {isMastered && (
                                <Tooltip title={intl.formatMessage({ id: 'form.label.masteredTooltip' })}>
                                  <Chip
                                    icon={<TrophyOutlined style={{ fontSize: '0.9rem' }} />}
                                    label={intl.formatMessage({ id: 'form.label.mastered' })}
                                    size="small"
                                    color="warning"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                          {dateRange.map((date) => {
                            const dateKey = format(date, 'yyyy-MM-dd');
                            const response = mergedHistory[dateKey]?.[target.id];
                            return (
                              <TableCell
                                key={dateKey}
                                align="center"
                                sx={{
                                  bgcolor:
                                    format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') ? 'primary.lighter' : 'background.paper'
                                }}
                              >
                                {renderResponseIcon(response)}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Summary Stats */}
            <Stack direction="row" spacing={3} sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckOutlined style={{ color: theme.palette.success.main }} />
                <Typography variant="body2">{intl.formatMessage({ id: 'common.correct' })}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CloseOutlined style={{ color: theme.palette.error.main }} />
                <Typography variant="body2">{intl.formatMessage({ id: 'common.incorrect' })}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <MinusOutlined style={{ color: theme.palette.grey[400] }} />
                <Typography variant="body2">{intl.formatMessage({ id: 'common.notAvailable' })}</Typography>
              </Stack>
            </Stack>
          </>
        )}
      </Box>

      {/* Image Popup Dialog - Only Image (3/4 of screen) */}
      <Dialog
        open={imagePopup.open}
        onClose={handleCloseImagePopup}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            m: 0
          }
        }}
      >
        {imagePopup.target && (
          <Box
            component="img"
            src={imagePopup.target.image}
            alt={imagePopup.target.name}
            sx={{
              width: '75vw',
              height: '75vh',
              objectFit: 'contain',
              borderRadius: 2,
              display: 'block'
            }}
          />
        )}
      </Dialog>
    </MainCard>
  );
};

ColdProbeMode.propTypes = {
  viewMode: PropTypes.string,
  onDataChange: PropTypes.func,
  targets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      image: PropTypes.string
    })
  ),
  // Offline mode props
  isOfflineMode: PropTypes.bool,
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  childName: PropTypes.string,
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistName: PropTypes.string,
  onSave: PropTypes.func,
  onBack: PropTypes.func
};

export default ColdProbeMode;
